import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { authActions } from 'app/core/state/actions/auth.actions';

import { Observable, from, fromEventPattern, merge, of } from 'rxjs';
import { catchError, exhaustMap, filter, map, mergeMap, startWith, switchMap, tap, timeout, withLatestFrom } from 'rxjs/operators';

import { ActionCodeSettings, FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { App } from '@capacitor/app';
import { SnackService } from 'app/app-wrapper/snack.service';
import { ApiService } from 'app/core/api/api.service';
import { TokenStorageService } from 'app/core/api/token-storage.service';
import { WebSocketService } from 'app/core/api/web-socket.service';
import { BiometricService } from 'app/core/auth/services/biometric.service';
import { SessionSyncService } from 'app/core/auth/session-sync.service';
import { AuthStrategy } from 'app/core/auth/strategies/auth.strategy';
import { entityRepo, outboxRepo } from 'app/core/db/offline-database';
import * as fromAuth from '../reducers/auth.reducers';
import { environment } from '../../../../environments/environment';


@Injectable()
export class AuthEffects {
    private refreshTimerSub?: any;

    constructor(
        private actions$: Actions,
        private store: Store,
        private api: ApiService,
        private strategy: AuthStrategy,
        private ws: WebSocketService,
        private tokens: TokenStorageService,
        private sync: SessionSyncService,
        private router: Router,
        private snack: SnackService,
    ) { }

    // ---------- Initialize ----------
    // core/auth/state/effects/authActions.effects.ts
    initialize$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.initialize),
            mergeMap(() => {
                const at = this.tokens.accessToken;
                if (at) {
                    this.ws.initConnection(at);
                    return of(authActions.assumeLoggedIn(), authActions.getCurrentUser());
                }
                return of(authActions.initialized(), authActions.loggedOut());
            })
        )
    );


    authStateChanges$ = createEffect(() => {
        const authState$ = merge(
            from(FirebaseAuthentication.getCurrentUser()).pipe(
                switchMap(res => of(res.user ?? null))
            ),
            fromEventPattern<{ user: any | null }>(
                handler => FirebaseAuthentication.addListener('authStateChange', handler),
                (_, listener) => listener.remove()
            ).pipe(
                switchMap(event => of(event.user ?? null))
            )
        ).pipe(startWith(null));

        return authState$.pipe(
            withLatestFrom(this.store.select(fromAuth.getAuthInitialized)),
            filter(([_, initialized]) => !initialized),
            switchMap(([user]) => {
                const firebaseUser = user as any | null;

                return of(
                    firebaseUser
                        ? authActions.getFirebaseUserSuccess({ firebaseUser })
                        : authActions.getFirebaseUserFailure({ error: null }),
                    authActions.setLoggedIn({ payload: !!firebaseUser }),
                );
            })
        );
    });

    // ---------- Get Current User ----------
    getCurrentUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.getCurrentUser),
            switchMap(() =>
                this.api.getCurrentUser().pipe(
                    mergeMap(user =>
                        of(
                            authActions.getCurrentUserSuccess({ user }),
                            authActions.initialized()
                        )
                    ),
                    catchError(err => {
                        const status = err?.status ?? 0;

                        if (status === 401) {
                            return of(
                                authActions.getCurrentUserError({ status }),
                                authActions.logout(),
                                authActions.initialized()
                            );
                        }

                        return of(
                            authActions.getCurrentUserError({ status }),
                            authActions.initialized()
                        );
                    })
                )
            )
        )
    );

    saveUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.saveUser),
            switchMap(({ user }) =>
                this.api.saveUser(user).pipe(
                    map((user) => authActions.saveUserSuccess({ user })),
                    tap(() => {
                        this.snack.success('Saved');
                    })
                )
            )
        )
    );

    updateProfilePicture$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.saveProfilePicture),
            switchMap(({ file }) =>
                this.api.updateProfilePictureUser(file).pipe(
                    map((user) => authActions.saveProfilePictureSuccess({ user })),
                    tap(() => {
                        this.snack.success('APP.USER_SETTINGS.UPLOAD_PROFILE_PICTURE');
                    })

                )
            )
        )
    );

    resetPassword$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.resetPassword),
            switchMap(({ email }) =>
                this.strategy.resetPassword(email).pipe(
                    map(() => authActions.resetPasswordSuccess()),
                    tap(() => {
                        this.snack.success('APP.USER_SETTINGS.RESET_PASSWORD');
                    })
                )
            )
        )
    );


    deleteUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.deleteUser),
            switchMap(({ userId }) =>
                this.strategy.deleteUser(userId).pipe(
                    map(() => authActions.deleteUserSuccess()),
                )
            )
        )
    );

    createUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.createUser),
            exhaustMap(({ email, password }) =>
                from(FirebaseAuthentication.createUserWithEmailAndPassword({ email, password })).pipe(
                    switchMap(res => {
                        if (!res.user) throw new Error('No user returned from Firebase');
                        return from(FirebaseAuthentication.sendEmailVerification());
                    }),
                    mergeMap(() =>
                        of(
                            authActions.createUserSuccess({ email }),
                            authActions.sendEmailVerificationSuccess()
                        )
                    ),
                    catchError(err => {
                        return of(
                            authActions.createUserError({
                                message: err?.code ?? 'Registration failed',
                            })
                        );
                    })
                )
            )
        )
    );

    createUserAtVerification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.createUserAtVerification),
            switchMap(({ inviteId, user }) =>
                this.api.verifyUser(inviteId, user).pipe(
                    map(() => authActions.createUserAtVerificationSuccess()),
                    catchError(err =>
                        of(
                            authActions.createUserAtVerificationError({
                                message: err?.message ?? 'Verification failed'
                            })
                        )
                    )
                )
            )
        )
    );

    createUserAtVerificationSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.createUserAtVerificationSuccess),
            tap(() => {
                this.snack.success('Account verified successfully. Please login to continue.');
                this.router.navigate(['/app/login']);
            })
        ), { dispatch: false }
    );


    login$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.login),
            exhaustMap(({ email, password }) =>
                from(FirebaseAuthentication.signInWithEmailAndPassword({ email, password })).pipe(
                    switchMap(res => {
                        if (!res.user) throw new Error('No user returned');
                        return from(FirebaseAuthentication.getIdToken());
                    }),
                    switchMap(tokenRes =>
                        from(this.tokens.setTokens({ accessToken: tokenRes.token, refreshToken: '' })).pipe(
                            tap(() => this.ws.initConnection(tokenRes.token)),
                            map(() =>
                                authActions.loginSuccess({
                                    accessToken: tokenRes.token,
                                    refreshToken: '',
                                })
                            )
                        )
                    ),
                    catchError(err =>
                        of(
                            authActions.loginError({
                                message: err?.code ?? 'Login fehlgeschlagen',
                            })
                        )
                    )
                )
            )
        )
    );

    loginSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.loginSuccess),
            switchMap(() => {
                return of(authActions.getCurrentUser());
            })
        )
    );

    socialLogin$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.socialLogin),
            switchMap(({ provider }) => {
                let loginPromise: Promise<any>;
                console.log('Social login with provider:', provider);
                switch (provider) {
                    case 'google':
                        loginPromise = FirebaseAuthentication.signInWithGoogle({}).then(
                            res => { console.log('Google sign-in result:', res); return res; }
                        ).catch((e: any) => {
                            console.error('❌ raw', e);
                            console.error('❌ keys', Object.keys(e ?? {}));
                            console.error('❌ stringified', JSON.stringify(e, Object.getOwnPropertyNames(e)));
                            alert(
                                'code=' + (e?.code ?? '-') +
                                '\nmessage=' + (e?.message ?? '-') +
                                '\ndetails=' + (e?.error ?? e?.details ?? '-')
                            );
                        });
                        break;
                    case 'facebook':
                        loginPromise = FirebaseAuthentication.signInWithFacebook();
                        break;
                    case 'apple':
                        loginPromise = FirebaseAuthentication.signInWithApple();
                        break;
                    default:
                        return of(authActions.socialLoginError({ message: 'Unsupported provider' }));
                }

                return from(loginPromise).pipe(
                    switchMap(res => {
                        const accessToken = res?.credential?.idToken;
                        const refreshToken = res?.credential?.refreshToken ?? '';
                        if (!accessToken) throw new Error('No ID token received from provider');

                        return from(this.tokens.setTokens({ accessToken, refreshToken })).pipe(
                            map(() => authActions.socialLoginSuccess({ accessToken, refreshToken }))
                        );
                    }),
                    catchError(err => {
                        console.error('Social login error:', err);
                        return of(
                            authActions.socialLoginError({
                                message: err?.code ?? 'Social login failed',
                            })
                        );
                    })

                );
            })
        )
    );

    socialLoginSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.socialLoginSuccess),
            switchMap(() => {
                return of(authActions.getCurrentUser());
            })
        )
    );

    // ---------- Logout ----------
    logout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.logout),
            switchMap(() =>
                from(this.tokens.clear()).pipe(
                    tap(() => from(entityRepo.dropTable())),
                    tap(() => from(outboxRepo.dropTable()))
                ).pipe(
                    tap(() => {
                        if (this.refreshTimerSub) this.refreshTimerSub.unsubscribe?.();
                    }),
                    tap(() => this.ws.disconnect()),
                    tap(() => from(FirebaseAuthentication.signOut())),
                    map(() => authActions.loggedOut())
                ),
            )
        )
    );

    // ---------- Navigation ----------
    navigateOnLogin$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(authActions.getCurrentUserSuccess),
                tap(() => {
                    const url = this.router.url;
                    const onLoginLikePage =
                        url === '/app' ||
                        url === '/app/' ||
                        url.startsWith('/app/login') ||
                        url.startsWith('/app/session-expired');

                    if (onLoginLikePage) {
                        this.router.navigate(['/app/']);
                    }
                })
            ),
        { dispatch: false }
    );

    // Nach Logout -> /app/login
    navigateOnLogout$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(authActions.loggedOut),
                tap(() => this.router.navigate(['/app/login']))
            ),
        { dispatch: false }
    );

    // 401 beim getCurrentUser -> Login + WS trennen
    navOn401$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(authActions.getCurrentUserError),
                filter(({ status }) => status === 401),
                tap(() => {
                    this.ws.disconnect();
                    this.router.navigate(['/app/login']);
                })
            ),
        { dispatch: false }
    );



    // Eingehend: Logout aus anderem Tab -> lokalen Logout auslösen
    logoutIn$ = createEffect(() =>
        this.sync.logout$.pipe(
            map(() => authActions.logout())
        )
    );


    // ---------- App Resume (Capacitor) ----------
    appResumeSource$ = createEffect(() =>
        new Observable<{ isActive: boolean }>(sub => {
            let active = true;
            App.addListener('appStateChange', (ev) => {
                if (active) sub.next(ev);
            }).then(handle => {
                if (!active) handle.remove();
                else sub.add(() => handle.remove());
            });
            return () => { active = false; };
        }).pipe(
            filter(ev => ev.isActive === true),
            map(() => authActions.appBecameActive())
        )
    );

    getActionCodeSettings(): ActionCodeSettings {

        switch (environment.environmentName) {
            case 'prod': {
                return {
                    url: 'https://ammsas-app.com/login',
                    handleCodeInApp: true,
                    iOS: {
                        bundleId: 'com.noricainformatics.ammsas'
                    },
                    android: {
                        packageName: 'com.noricainformatics.ammsas',
                        installApp: true,
                        minimumVersion: '12'
                    }
                }
            }
            case 'stage': {
                return {
                    url: 'https://stage.ammsas-app.com/login',
                    handleCodeInApp: true,
                    iOS: {
                        bundleId: 'com.noricainformatics.ammsas.stage'
                    },
                    android: {
                        packageName: 'com.noricainformatics.ammsas.stage',
                        installApp: true,
                        minimumVersion: '12'
                    }
                }
            }
            case 'dev': {
                return {
                    url: 'https://dev.ammsas-app.com/login',
                    handleCodeInApp: true,
                    iOS: {
                        bundleId: 'com.noricainformatics.ammsas.dev'
                    },
                    android: {
                        packageName: 'com.noricainformatics.ammsas.dev',
                        installApp: true,
                        minimumVersion: '12'
                    }
                }
            }
            default: {
                return {
                    url: 'https://dev.ammsas-app.com/login',
                    handleCodeInApp: true,
                    iOS: {
                        bundleId: 'com.noricainformatics.ammsas.dev'
                    },
                    android: {
                        packageName: 'com.noricainformatics.ammsas.dev',
                        installApp: true,
                        minimumVersion: '12'
                    }
                }
            }
        }
    }

}
